/**
 * Canvas-based splice logo generator.
 * Combines a farm logo and customer logo into a circular image
 * split diagonally at 45° — farm logo top-left, customer logo bottom-right.
 */

const SIZE = 512
const HALF = SIZE / 2

/**
 * Load an image from a URL with CORS support.
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
    img.src = url
  })
}

/**
 * Draw an image with contain-fit scaling (like CSS object-fit: contain).
 * Centres the image within the target rect with padding, no cropping.
 */
function drawImageContain(ctx, img, x, y, w, h, padding = 0) {
  const availW = w - padding * 2
  const availH = h - padding * 2
  const imgRatio = img.width / img.height
  const targetRatio = availW / availH

  let dw, dh

  if (imgRatio > targetRatio) {
    // Image is wider — fit to width
    dw = availW
    dh = availW / imgRatio
  } else {
    // Image is taller — fit to height
    dh = availH
    dw = availH * imgRatio
  }

  const dx = x + padding + (availW - dw) / 2
  const dy = y + padding + (availH - dh) / 2

  ctx.drawImage(img, 0, 0, img.width, img.height, dx, dy, dw, dh)
}

/**
 * Detect the dominant background colour of an image by sampling corner pixels.
 * Returns a CSS colour string like "rgb(r, g, b)".
 */
function getDominantBgColor(img) {
  const sampleCanvas = document.createElement('canvas')
  sampleCanvas.width = img.width
  sampleCanvas.height = img.height
  const sCtx = sampleCanvas.getContext('2d')
  sCtx.drawImage(img, 0, 0)

  const w = img.width
  const h = img.height
  // Sample corners and edge midpoints
  const points = [
    [0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1],
    [Math.floor(w / 2), 0], [Math.floor(w / 2), h - 1],
    [0, Math.floor(h / 2)], [w - 1, Math.floor(h / 2)]
  ]

  let rSum = 0, gSum = 0, bSum = 0, count = 0
  for (const [x, y] of points) {
    const pixel = sCtx.getImageData(x, y, 1, 1).data
    // Skip fully transparent pixels
    if (pixel[3] < 128) continue
    rSum += pixel[0]
    gSum += pixel[1]
    bSum += pixel[2]
    count++
  }

  if (count === 0) return '#ffffff'

  const r = Math.round(rSum / count)
  const g = Math.round(gSum / count)
  const b = Math.round(bSum / count)
  return `rgb(${r}, ${g}, ${b})`
}

/**
 * Generate a spliced logo combining farm and customer logos.
 * @param {string} farmLogoUrl - Public URL of the farm logo
 * @param {string} customerLogoUrl - Public URL of the customer logo
 * @param {Object} [options]
 * @param {boolean} [options.showDivider=true] - Draw a white diagonal separator
 * @returns {Promise<Blob>} PNG blob of the spliced logo
 */
export async function generateSplicedLogo(farmLogoUrl, customerLogoUrl, options = {}) {
  const { showDivider = true } = options

  const [farmImg, customerImg] = await Promise.all([
    loadImage(farmLogoUrl),
    loadImage(customerLogoUrl)
  ])

  const PADDING = 20 // px inset so logos don't touch the edges

  // Detect background colours from each logo
  const farmBgColor = getDominantBgColor(farmImg)
  const customerBgColor = getDominantBgColor(customerImg)

  const canvas = document.createElement('canvas')
  canvas.width = SIZE
  canvas.height = SIZE
  const ctx = canvas.getContext('2d')

  // Draw farm logo in top-left triangle (0,0 → SIZE,0 → 0,SIZE)
  ctx.save()
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(SIZE, 0)
  ctx.lineTo(0, SIZE)
  ctx.closePath()
  ctx.clip()
  ctx.fillStyle = farmBgColor
  ctx.fillRect(0, 0, SIZE, SIZE)
  drawImageContain(ctx, farmImg, 0, 0, SIZE, SIZE, PADDING)
  ctx.restore()

  // Draw customer logo in bottom-right triangle (SIZE,0 → SIZE,SIZE → 0,SIZE)
  ctx.save()
  ctx.beginPath()
  ctx.moveTo(SIZE, 0)
  ctx.lineTo(SIZE, SIZE)
  ctx.lineTo(0, SIZE)
  ctx.closePath()
  ctx.clip()
  ctx.fillStyle = customerBgColor
  ctx.fillRect(0, 0, SIZE, SIZE)
  drawImageContain(ctx, customerImg, 0, 0, SIZE, SIZE, PADDING)
  ctx.restore()

  // Optional white diagonal divider
  if (showDivider) {
    ctx.save()
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.moveTo(SIZE, 0)
    ctx.lineTo(0, SIZE)
    ctx.stroke()
    ctx.restore()
  }

  // Apply circular mask via destination-in
  ctx.save()
  ctx.globalCompositeOperation = 'destination-in'
  ctx.beginPath()
  ctx.arc(HALF, HALF, HALF, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
      } else {
        reject(new Error('Failed to generate spliced logo'))
      }
    }, 'image/png')
  })
}

/**
 * Download a blob as a file.
 * @param {Blob} blob
 * @param {string} customerName
 */
export function downloadSplicedLogo(blob, customerName) {
  const safeName = customerName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${safeName}-splice-logo.png`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
